import { useEffect, useMemo, useState } from "react";
import { propertyDisplayLabel } from "@/lib/propertyDisplay";
import { propertiesService } from "@/services/propertiesService";
import type { LeadSummary } from "@/types/entities";

export interface LeadPropertyName {
  loading: boolean;
  name: string | null;
}

const propertyNameCache = new Map<string, string>();

/** Resolve the property ids on the current leads page in one batched request. */
export function useLeadPropertyNames(rows: LeadSummary[]): Record<string, LeadPropertyName> {
  const propertyIdsKey = useMemo(
    () =>
      [...new Set(rows.map((row) => row.propertyId?.trim()).filter((id): id is string => Boolean(id)))]
        .sort()
        .join(","),
    [rows],
  );
  const propertyIds = useMemo(
    () => (propertyIdsKey ? propertyIdsKey.split(",") : []),
    [propertyIdsKey],
  );
  const [names, setNames] = useState<Record<string, LeadPropertyName>>({});

  useEffect(() => {
    const initial = Object.fromEntries(
      propertyIds.map((id) => {
        const cached = propertyNameCache.get(id);
        return [id, { loading: cached === undefined, name: cached ?? null }];
      }),
    );
    setNames(initial);

    const unresolvedIds = propertyIds.filter((id) => !propertyNameCache.has(id));
    if (unresolvedIds.length === 0) return;

    let cancelled = false;
    void propertiesService
      .listByIds(unresolvedIds)
      .then((properties) => {
        if (cancelled) return;
        const resolved = new Map(
          properties.map((property) => [property.id, propertyDisplayLabel(property)]),
        );
        for (const [id, name] of resolved) propertyNameCache.set(id, name);
        setNames(
          Object.fromEntries(
            propertyIds.map((id) => [
              id,
              { loading: false, name: propertyNameCache.get(id) ?? null },
            ]),
          ),
        );
      })
      .catch(() => {
        if (cancelled) return;
        setNames((previous) => {
          const next = { ...previous };
          for (const id of unresolvedIds) {
            next[id] = { loading: false, name: propertyNameCache.get(id) ?? null };
          }
          return next;
        });
      });

    return () => {
      cancelled = true;
    };
  }, [propertyIds]);

  return names;
}
